import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { ArrowLeft, User, Calendar, CreditCard, Edit2, Save, ClipboardCheck, Plus, Crown, Gift } from "lucide-react";
import { format } from "date-fns";
import { safeFormat } from "@/lib/dateUtils";
import ConsultationForm from "@/components/ConsultationForm";

export default function ClientProfile() {
  const { clientId } = useParams();
  const [client, setClient] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [consultationForms, setConsultationForms] = useState([]);
  const [showNewForm, setShowNewForm] = useState(false);
  const [emailHistory, setEmailHistory] = useState([]);
  const [rewards, setRewards] = useState([]);
  const [rewardsLoading, setRewardsLoading] = useState(false);
  const [issuingReward, setIssuingReward] = useState(false);
  const [nailLengthOptions, setNailLengthOptions] = useState([]);

  useEffect(() => {
    const load = async () => {
      try {
        const [clientRes, formsRes, emailRes, rewardsRes, settingsRes] = await Promise.all([
          axios.get(`${API}/clients/${clientId}`),
          axios.get(`${API}/consultation-forms?client_id=${clientId}`),
          axios.get(`${API}/automations/email-history?client_id=${clientId}`),
          axios.get(`${API}/rewards/history/${clientId}`),
          axios.get(`${API}/settings?lite=true`),
        ]);
        setClient(clientRes.data.client);
        setAppointments(clientRes.data.appointments || []);
        setEditData(clientRes.data.client);
        setConsultationForms(formsRes.data.forms || []);
        setEmailHistory(emailRes.data.history || []);
        setRewards(rewardsRes.data.rewards || []);
        setNailLengthOptions(settingsRes.data.nail_lengths || []);
      } catch (e) {  }
    };
    load();
  }, [clientId]);

  const refreshRewards = async () => {
    setRewardsLoading(true);
    try {
      const res = await axios.get(`${API}/rewards/history/${clientId}`);
      setRewards(res.data.rewards || []);
    } catch (e) { /* ignore */ }
    setRewardsLoading(false);
  };

  const refreshEmailHistory = async () => {
    try {
      const res = await axios.get(`${API}/automations/email-history?client_id=${clientId}`);
      setEmailHistory(res.data.history || []);
    } catch (e) { /* ignore */ }
  };

  const issueRewardManually = async () => {
    if (!window.confirm('Issue a new VIP reward to this client right now? They will receive an email with the voucher code.')) return;
    setIssuingReward(true);
    try {
      const res = await axios.post(`${API}/rewards/issue/${clientId}`);
      if (res.data.success) {
        toast.success(`Reward issued: ${res.data.voucher?.code || ''}`);
        await Promise.all([refreshRewards(), refreshEmailHistory()]);
      } else {
        toast.error(res.data.error || 'Could not issue reward');
      }
    } catch (e) {
      toast.error('Failed to issue reward');
    }
    setIssuingReward(false);
  };

  const saveClient = async () => {
    try {
      const res = await axios.put(`${API}/clients/${clientId}`, {
        name: editData.name,
        email: editData.email,
        phone: editData.phone,
        status: editData.status,
        notes: editData.notes,
        preferences: editData.preferences,
        tags: editData.tags,
        verified_nail_length: editData.verified_nail_length || null,
        cash_only: !!editData.cash_only
      });
      setClient(res.data);
      setEditData(res.data);
      setEditing(false);
      toast.success('Client updated');
    } catch (e) { toast.error('Failed to update'); }
  };

  if (!client) return <div className="flex items-center justify-center p-12"><div className="animate-pulse text-muted-foreground">Loading...</div></div>;

  const statusColors = {
    Active: 'bg-green-100 text-green-700',
    Inactive: 'bg-gray-100 text-gray-700',
    OnHold: 'bg-yellow-100 text-yellow-700',
    VIP: 'bg-purple-100 text-purple-700'
  };

  return (
    <div className="space-y-6 fade-in">
      <Toaster position="top-center" />
      <div className="flex items-center gap-3">
        <Link to="/admin/clients">
          <Button variant="ghost" size="icon" className="rounded-xl"><ArrowLeft size={18} /></Button>
        </Link>
        <div className="flex-1">
          <h1 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: 'Playfair Display' }}>{client.name}</h1>
          <p className="text-sm text-muted-foreground">{client.email}</p>
        </div>
        <Badge className={`${statusColors[client.status] || 'bg-gray-100'} rounded-full`}>{client.status}</Badge>
        {client.cash_only && (
          <Badge className="bg-emerald-100 text-emerald-700 rounded-full" data-testid="client-cash-only-badge">💵 Cash only</Badge>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {[
          { label: 'Appointments', value: client.total_appointments || 0 },
          { label: 'Total Spent', value: `£${(client.total_spent || 0).toFixed(2)}` },
          {
            label: 'Last Visit',
            value: client.last_appointment
              ? safeFormat(client.last_appointment, 'd MMM yyyy')
              : '—',
            sub: client.days_since !== null && client.days_since !== undefined
              ? (client.days_since === 0 ? 'today' : `${client.days_since}d ago`)
              : null,
            testid: 'stat-last-visit',
          },
          { label: '3-Week Date', value: client.three_week_date ? safeFormat(client.three_week_date, 'd MMM') : '-' },
          { label: '4-Week Date', value: client.four_week_date ? safeFormat(client.four_week_date, 'd MMM') : '-' },
        ].map((stat, i) => (
          <Card key={i} className="p-3 rounded-2xl border" data-testid={stat.testid}>
            <p className="text-xs text-muted-foreground">{stat.label}</p>
            <p className="text-lg font-semibold tabular-nums" style={{ fontFamily: 'Playfair Display' }}>{stat.value}</p>
            {stat.sub && <p className="text-[11px] text-muted-foreground mt-0.5">{stat.sub}</p>}
          </Card>
        ))}
      </div>

      <Tabs defaultValue="history">
        <TabsList className="rounded-xl">
          <TabsTrigger value="history" className="rounded-lg">Visit History</TabsTrigger>
          <TabsTrigger value="details" className="rounded-lg">Details & Notes</TabsTrigger>
          <TabsTrigger value="consultation" className="rounded-lg flex items-center gap-1">
            <ClipboardCheck size={13} /> Forms
            {consultationForms.length > 0 && <Badge className="ml-1 text-[10px] h-4 px-1 bg-primary text-white rounded-full">{consultationForms.length}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="emails" className="rounded-lg flex items-center gap-1">
            Emails
            {emailHistory.length > 0 && <Badge className="ml-1 text-[10px] h-4 px-1 bg-primary text-white rounded-full">{emailHistory.length}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="rewards" className="rounded-lg flex items-center gap-1" data-testid="tab-rewards">
            <Crown size={13} className="text-amber-500" /> Rewards
            {rewards.length > 0 && <Badge className="ml-1 text-[10px] h-4 px-1 bg-amber-500 text-white rounded-full">{rewards.length}</Badge>}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="history" className="mt-4">
          <Card className="rounded-2xl border shadow-[0_10px_30px_rgba(43,18,27,0.06)] overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/30">
                  <th className="text-left p-3 font-medium text-muted-foreground">Date</th>
                  <th className="text-left p-3 font-medium text-muted-foreground">Services</th>
                  <th className="text-left p-3 font-medium text-muted-foreground">Total</th>
                  <th className="text-left p-3 font-medium text-muted-foreground">Status</th>
                  <th className="text-left p-3 font-medium text-muted-foreground">Payment</th>
                </tr>
              </thead>
              <tbody>
                {appointments.map(appt => (
                  <tr key={appt.id} className="border-b border-border/50">
                    <td className="p-3">{safeFormat(appt.appointment_datetime, 'EEE d MMM yyyy, HH:mm')}</td>
                    <td className="p-3">{(appt.services || []).map(s => s.name).join(', ')}</td>
                    <td className="p-3 font-medium">£{(appt.total_price || 0).toFixed(2)}</td>
                    <td className="p-3"><Badge className={`text-xs rounded-full ${appt.booking_status === 'Completed' ? 'bg-blue-100 text-blue-700' : appt.booking_status === 'Confirmed' ? 'bg-green-100 text-green-700' : 'bg-gray-100'}`}>{appt.booking_status}</Badge></td>
                    <td className="p-3"><Badge variant="outline" className="text-xs rounded-full">{appt.payment_status}</Badge></td>
                  </tr>
                ))}
                {appointments.length === 0 && <tr><td colSpan="5" className="p-8 text-center text-muted-foreground">No visit history yet</td></tr>}
              </tbody>
            </table>
          </Card>
        </TabsContent>

        <TabsContent value="details" className="mt-4">
          <Card className="p-6 rounded-2xl border shadow-[0_10px_30px_rgba(43,18,27,0.06)]">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold" style={{ fontFamily: 'Playfair Display' }}>Client Details</h3>
              {!editing ? (
                <Button variant="ghost" size="sm" onClick={() => setEditing(true)} className="rounded-xl"><Edit2 size={14} className="mr-1" /> Edit</Button>
              ) : (
                <Button size="sm" onClick={saveClient} className="rounded-xl"><Save size={14} className="mr-1" /> Save</Button>
              )}
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Name</Label>
                <Input value={editData.name || ''} onChange={e => setEditData(p => ({...p, name: e.target.value}))} disabled={!editing} className="mt-1 rounded-xl" />
              </div>
              <div>
                <Label>Email</Label>
                <Input value={editData.email || ''} onChange={e => setEditData(p => ({...p, email: e.target.value}))} disabled={!editing} className="mt-1 rounded-xl" />
              </div>
              <div>
                <Label>Phone</Label>
                <Input value={editData.phone || ''} onChange={e => setEditData(p => ({...p, phone: e.target.value}))} disabled={!editing} className="mt-1 rounded-xl" />
              </div>
              <div>
                <Label>Status</Label>
                <Select value={editData.status || 'Active'} onValueChange={v => setEditData(p => ({...p, status: v}))} disabled={!editing}>
                  <SelectTrigger className="mt-1 rounded-xl"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {['Active', 'Inactive', 'OnHold', 'VIP'].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Verified nail length</Label>
                <p className="text-[11px] text-muted-foreground mb-1">What you actually saw in person on their last visit — used to catch dishonest re-selections at their next booking.</p>
                {nailLengthOptions.length > 0 ? (
                  <Select value={editData.verified_nail_length || ''} onValueChange={v => setEditData(p => ({...p, verified_nail_length: v}))} disabled={!editing}>
                    <SelectTrigger className="mt-1 rounded-xl" data-testid="client-verified-length-select"><SelectValue placeholder="Not set" /></SelectTrigger>
                    <SelectContent>
                      {nailLengthOptions.map(o => <SelectItem key={o.name} value={o.name}>{o.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input value={editData.verified_nail_length || ''} onChange={e => setEditData(p => ({...p, verified_nail_length: e.target.value}))} disabled={!editing} placeholder="e.g. Medium" className="mt-1 rounded-xl" data-testid="client-verified-length-input" />
                )}
              </div>
              <div className="md:col-span-2 flex items-center justify-between p-3 bg-emerald-50 border border-emerald-200 rounded-xl">
                <div>
                  <Label className="text-sm">Cash only client</Label>
                  <p className="text-[11px] text-muted-foreground mt-0.5">Loyal / trusted clients skip the online deposit — no Stripe payment required at checkout, they pay in person as usual.</p>
                </div>
                <Switch
                  checked={!!editData.cash_only}
                  onCheckedChange={v => setEditData(p => ({ ...p, cash_only: v }))}
                  disabled={!editing}
                  data-testid="client-cash-only-toggle"
                />
              </div>
              <div className="md:col-span-2">
                <Label>Preferences</Label>
                <Textarea value={editData.preferences || ''} onChange={e => setEditData(p => ({...p, preferences: e.target.value}))} disabled={!editing} className="mt-1 rounded-xl" rows={2} />
              </div>
              <div className="md:col-span-2">
                <Label>Notes</Label>
                <Textarea value={editData.notes || ''} onChange={e => setEditData(p => ({...p, notes: e.target.value}))} disabled={!editing} className="mt-1 rounded-xl" rows={3} />
              </div>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="consultation" className="mt-4">
          {showNewForm ? (
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold" style={{ fontFamily: 'Playfair Display' }}>New Consultation Form</h3>
                <Button variant="ghost" size="sm" onClick={() => setShowNewForm(false)} className="rounded-xl">Cancel</Button>
              </div>
              <ConsultationForm 
                clientId={clientId} 
                clientName={client?.name} 
                clientEmail={client?.email}
                compact={true}
                onComplete={() => {
                  setShowNewForm(false);
                  // Reload forms
                  axios.get(`${API}/consultation-forms?client_id=${clientId}`).then(r => setConsultationForms(r.data.forms || []));
                  toast.success('Consultation form saved');
                }} 
              />
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground">{consultationForms.length} form{consultationForms.length !== 1 ? 's' : ''} on file</p>
                <Button onClick={() => setShowNewForm(true)} size="sm" className="rounded-xl">
                  <Plus size={14} className="mr-1" /> New Form
                </Button>
              </div>
              {consultationForms.length === 0 ? (
                <Card className="p-8 rounded-2xl text-center">
                  <ClipboardCheck size={40} className="mx-auto text-muted-foreground/30 mb-3" />
                  <p className="text-sm text-muted-foreground">No consultation forms yet.</p>
                  <Button onClick={() => setShowNewForm(true)} variant="secondary" size="sm" className="mt-3 rounded-xl">
                    Create consultation form
                  </Button>
                </Card>
              ) : (
                consultationForms.map(form => (
                  <Card key={form.id} className="p-5 rounded-2xl border shadow-[0_10px_30px_rgba(43,18,27,0.06)]">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <ClipboardCheck size={16} className="text-primary" />
                        <h4 className="font-semibold text-sm" style={{ fontFamily: 'Playfair Display' }}>Consultation Form</h4>
                      </div>
                      <Badge variant="secondary" className="text-xs rounded-full">{safeFormat(form.created_at, 'd MMM yyyy')}</Badge>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-xs">
                      {form.dob && <div><span className="text-muted-foreground">DOB:</span> <span className="font-medium">{form.dob}</span></div>}
                      {form.contact_number && <div><span className="text-muted-foreground">Phone:</span> <span className="font-medium">{form.contact_number}</span></div>}
                      {form.emergency_contact && <div><span className="text-muted-foreground">Emergency:</span> <span className="font-medium">{form.emergency_contact}</span></div>}
                      {form.allergies && <div className="col-span-full"><span className="text-muted-foreground">Allergies:</span> <span className="font-medium text-red-600">{form.allergies}</span></div>}
                      {form.medical_history && <div className="col-span-full"><span className="text-muted-foreground">Medical History:</span> <span className="font-medium">{form.medical_history}</span></div>}
                      {form.current_medication && <div className="col-span-full"><span className="text-muted-foreground">Medication:</span> <span className="font-medium">{form.current_medication}</span></div>}
                      {form.conditions && form.conditions.length > 0 && (
                        <div className="col-span-full">
                          <span className="text-muted-foreground">Conditions:</span>{' '}
                          {form.conditions.map((c, i) => <Badge key={i} variant="outline" className="text-[10px] mr-1 mb-1 rounded-full">{c}</Badge>)}
                        </div>
                      )}
                      {form.occupation_hobbies && <div className="col-span-full"><span className="text-muted-foreground">Occupation/Lifestyle:</span> <span className="font-medium">{form.occupation_hobbies}</span></div>}
                      {form.nail_aspirations && <div className="col-span-full"><span className="text-muted-foreground">Nail Goals:</span> <span className="font-medium">{form.nail_aspirations}</span></div>}
                    </div>
                    {form.technician_name && (
                      <p className="text-xs text-muted-foreground mt-3 pt-2 border-t border-border">Completed by: {form.technician_name} on {form.date_signed}</p>
                    )}
                  </Card>
                ))
              )}
            </div>
          )}
        </TabsContent>

        <TabsContent value="emails" className="mt-4">
          {emailHistory.length === 0 ? (
            <Card className="p-8 rounded-2xl text-center">
              <p className="text-sm text-muted-foreground">No emails sent to this client yet.</p>
            </Card>
          ) : (
            <div className="space-y-2">
              {emailHistory.map(h => {
                const statusLabel = {
                  sent: 'Sent', sent_historical: 'Sent (historical)',
                  not_sent: 'Not sent', not_sent_historical: 'Not sent (historical)',
                  failed: 'Failed',
                }[h.status] || h.status;
                const statusCls =
                  h.status === 'sent' || h.status === 'sent_historical'
                    ? 'bg-green-100 text-green-700'
                    : h.status === 'failed'
                      ? 'bg-red-100 text-red-700'
                      : 'bg-amber-100 text-amber-800';
                const isVipReward = h.automation_id === 'vip_reward' || h.automation_name === 'VIP Reward';
                return (
                  <Card key={h.id} className={`p-4 rounded-xl border ${isVipReward ? 'border-amber-200 bg-amber-50/40' : ''}`} data-testid={`email-history-${h.id}`}>
                    <div className="flex items-center justify-between gap-2 mb-1 flex-wrap">
                      <div className="flex items-center gap-2 flex-wrap">
                        {isVipReward && (
                          <Badge className="text-[10px] rounded-full bg-amber-100 text-amber-800 border border-amber-300">
                            ✨ VIP Reward
                          </Badge>
                        )}
                        <p className="text-sm font-medium">{h.subject}</p>
                      </div>
                      <Badge className={`text-[10px] rounded-full ${statusCls}`}>{statusLabel}</Badge>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {h.automation_name} · {safeFormat(h.created_at, 'd MMM yyyy, HH:mm')}
                      {h.voucher_code && (
                        <span className="ml-2 font-mono text-amber-700">{h.voucher_code}</span>
                      )}
                    </p>
                    {h.body && (
                      <details className="mt-2">
                        <summary className="text-xs text-primary cursor-pointer hover:underline">View details</summary>
                        <pre className="mt-1 text-xs text-muted-foreground whitespace-pre-wrap bg-muted/30 p-2 rounded-lg">{h.body}</pre>
                        {h.error && <p className="mt-1 text-xs text-red-600">Error: {h.error}</p>}
                      </details>
                    )}
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        <TabsContent value="rewards" className="mt-4">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <div>
              <p className="text-sm font-semibold" style={{ fontFamily: 'Playfair Display' }}>VIP Reward History</p>
              <p className="text-xs text-muted-foreground">Every reward issued to this client — auto + manual.</p>
            </div>
            <Button
              size="sm"
              data-testid="manual-issue-reward-btn"
              onClick={issueRewardManually}
              disabled={issuingReward}
              className="rounded-xl bg-amber-500 hover:bg-amber-600 text-white"
            >
              <Crown size={13} className="mr-1.5" /> {issuingReward ? 'Issuing…' : 'Issue Reward Now'}
            </Button>
          </div>

          {rewardsLoading ? (
            <Card className="p-8 rounded-2xl text-center text-sm text-muted-foreground">Loading…</Card>
          ) : rewards.length === 0 ? (
            <Card className="p-8 rounded-2xl text-center">
              <Gift size={28} className="mx-auto text-muted-foreground/40 mb-2" />
              <p className="text-sm text-muted-foreground">No rewards issued to this client yet.</p>
              <p className="text-xs text-muted-foreground mt-1">Use "Issue Reward Now" to send a one-off, or rewards will be auto-issued at each VIP milestone.</p>
            </Card>
          ) : (
            <div className="space-y-2">
              {rewards.map(r => {
                const statusCls = r.display_status === 'Active' ? 'bg-green-100 text-green-700 border-green-200'
                  : r.display_status === 'Redeemed' ? 'bg-blue-100 text-blue-700 border-blue-200'
                  : 'bg-muted/40 text-muted-foreground border-border';
                const valueLabel = r.discount_type === 'percent'
                  ? `${r.discount_percent}% off`
                  : r.discount_type === 'free_service'
                    ? 'Free service'
                    : `£${Number(r.initial_value || 0).toFixed(0)}`;
                return (
                  <Card key={r.id} className={`p-4 rounded-xl border ${r.display_status === 'Active' ? 'border-amber-200 bg-amber-50/40' : ''}`} data-testid={`reward-row-${r.id}`}>
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
                          <Crown size={16} className="text-amber-500" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-semibold" style={{ fontFamily: 'Playfair Display' }}>{valueLabel}</span>
                            <span className="font-mono text-[11px] text-amber-700">{r.code}</span>
                          </div>
                          <p className="text-xs text-muted-foreground">
                            Issued {safeFormat(r.created_at, 'd MMM yyyy')}
                            {r.reward_milestone ? ` · visit #${r.reward_milestone}` : ''}
                            {r.expiry_date ? ` · expires ${safeFormat(r.expiry_date + 'T00:00:00Z', 'd MMM yyyy')}` : ''}
                          </p>
                          {r.display_status === 'Redeemed' && r.redeemed_appointment_id && (
                            <p className="text-xs text-blue-700 mt-0.5">
                              Redeemed {r.redeemed_at ? safeFormat(r.redeemed_at, 'd MMM yyyy') : ''}
                              {r.redeemed_amount ? ` · saved £${r.redeemed_amount.toFixed(2)}` : ''}
                              <Link
                                to={`/admin/appointments?focus=${r.redeemed_appointment_id}`}
                                data-testid={`reward-view-appt-${r.id}`}
                                className="ml-2 underline underline-offset-2 hover:text-blue-900"
                              >
                                View appointment →
                              </Link>
                            </p>
                          )}
                        </div>
                      </div>
                      <Badge className={`text-[10px] rounded-full border ${statusCls}`}>{r.display_status}</Badge>
                    </div>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

      </Tabs>
    </div>
  );
}
